import React from 'react';
import useToolStore from '../../../store/useToolStore';

const ApiGeneratorControls = ({ config, onChange }) => {
  const setIsLoading = useToolStore((state) => state.setIsLoading);
  const setApiResponse = useToolStore((state) => state.setApiResponse);
  const setApiError = useToolStore((state) => state.setApiError);
  const isLoading = useToolStore((state) => state.isLoading);
  
  const handleChange = (key, value) => {
    onChange({ [key]: value });
  };
  
  const testAPI = async () => {
    setIsLoading(true);
    setApiError(null);
    setApiResponse(null);
    
    try {
      // Build URL
      let url = '';
      if (config.url && config.url.startsWith('http')) {
        // Full URL provided
        url = config.url;
      } else if (config.baseURL) {
        // Build from baseURL + endpoint
        const endpoint = config.url || '';
        url = config.baseURL + (endpoint.startsWith('/') ? endpoint : '/' + endpoint);
      } else {
        throw new Error('Base URL is required');
      }
      
      // Add query parameters
      const queryParams = (config.queryParams || []).filter(p => p.key && p.value);
      if (queryParams.length > 0) {
        const queryString = queryParams.map(p => 
          `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`
        ).join('&');
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
      
      // Build headers
      const headers = {};
      (config.headers || []).forEach(h => {
        if (h.key && h.value) {
          headers[h.key] = h.value;
        }
      });
      
      // Build request options
      const options = {
        method: config.method || 'GET',
        headers: {
          ...headers,
        },
      };
      
      // Add body for POST, PUT, PATCH
      if (['POST', 'PUT', 'PATCH'].includes(config.method)) {
        if (config.bodyType === 'json' && config.bodyContent) {
          try {
            options.body = JSON.stringify(JSON.parse(config.bodyContent));
            if (!headers['Content-Type']) {
              options.headers['Content-Type'] = 'application/json';
            }
          } catch (e) {
            options.body = config.bodyContent;
          }
        } else if (config.bodyType === 'form' && config.bodyContent) {
          options.body = config.bodyContent;
          if (!headers['Content-Type']) {
            options.headers['Content-Type'] = 'application/x-www-form-urlencoded';
          }
        } else if (config.bodyType === 'multipart' && config.bodyContent) {
          options.body = config.bodyContent;
          // Note: For multipart, boundary should be set in Content-Type header
        }
      }
      
      // Make the request
      const response = await fetch(url, options);
      const responseText = await response.text();
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        responseData = responseText;
      }
      
      setApiResponse({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data: responseData,
        raw: responseText,
      });
    } catch (error) {
      setApiError({
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addHeader = () => {
    const newHeaders = [...(config.headers || []), { key: '', value: '' }];
    handleChange('headers', newHeaders);
  };

  const removeHeader = (index) => {
    const newHeaders = (config.headers || []).filter((_, i) => i !== index);
    handleChange('headers', newHeaders);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...(config.headers || [])];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    handleChange('headers', newHeaders);
  };

  const addQueryParam = () => {
    const newParams = [...(config.queryParams || []), { key: '', value: '' }];
    handleChange('queryParams', newParams);
  };

  const removeQueryParam = (index) => {
    const newParams = (config.queryParams || []).filter((_, i) => i !== index);
    handleChange('queryParams', newParams);
  };

  const updateQueryParam = (index, field, value) => {
    const newParams = [...config.queryParams];
    newParams[index] = { ...newParams[index], [field]: value };
    handleChange('queryParams', newParams);
  };

  return (
    <div className="space-y-6">
      {/* Base URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Base URL
        </label>
        <input
          type="text"
          value={config.baseURL}
          onChange={(e) => handleChange('baseURL', e.target.value)}
          className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
          placeholder="https://api.example.com"
        />
      </div>

      {/* HTTP Method */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          HTTP Method
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((method) => (
            <button
              key={method}
              onClick={() => handleChange('method', method)}
              className={`
                px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                ${
                  config.method === method
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {method}
            </button>
          ))}
        </div>
      </div>

      {/* Endpoint */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Endpoint
        </label>
        <input
          type="text"
          value={(() => {
            const url = config.url || '';
            const base = config.baseURL || '';
            if (!url) return '';
            if (url.startsWith('http')) {
              // Full URL, extract endpoint
              if (base && url.startsWith(base)) {
                return url.replace(base, '') || '/';
              }
              return url;
            }
            // Already an endpoint
            return url.startsWith('/') ? url : '/' + url;
          })()}
          onChange={(e) => {
            const endpoint = e.target.value;
            // Store as endpoint (not full URL)
            handleChange('url', endpoint);
          }}
          className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
          placeholder="/users"
        />
      </div>

      {/* Headers */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Headers
          </label>
          <button
            onClick={addHeader}
            className="px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap bg-gradient-primary text-white hover:opacity-90"
          >
            + Add Header
          </button>
        </div>
        <div className="space-y-2">
          {(config.headers || []).map((header, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={header.key}
                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                placeholder="Key"
                className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
              />
              <input
                type="text"
                value={header.value}
                onChange={(e) => updateHeader(index, 'value', e.target.value)}
                placeholder="Value"
                className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
              />
              <button
                onClick={() => removeHeader(index)}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-white text-sm"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Query Parameters */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Query Parameters
          </label>
          <button
            onClick={addQueryParam}
            className="px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap bg-gradient-primary text-white hover:opacity-90"
          >
            + Add Param
          </button>
        </div>
        <div className="space-y-2">
          {(config.queryParams || []).map((param, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={param.key}
                onChange={(e) => updateQueryParam(index, 'key', e.target.value)}
                placeholder="Key"
                className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
              />
              <input
                type="text"
                value={param.value}
                onChange={(e) => updateQueryParam(index, 'value', e.target.value)}
                placeholder="Value"
                className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
              />
              <button
                onClick={() => removeQueryParam(index)}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-white text-sm"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Body (for POST, PUT, PATCH) */}
      {['POST', 'PUT', 'PATCH'].includes(config.method) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Request Body Type
          </label>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {['json', 'form', 'multipart'].map((type) => (
              <button
                key={type}
                onClick={() => handleChange('bodyType', type)}
                className={`
                  px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                  ${
                    config.bodyType === type
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {type.toUpperCase()}
              </button>
            ))}
          </div>
          <textarea
            value={config.bodyContent}
            onChange={(e) => handleChange('bodyContent', e.target.value)}
            rows={6}
            className="w-full px-3 py-2 bg-gray-800 rounded text-white text-sm font-mono resize-none"
            placeholder={config.bodyType === 'json' ? '{\n  "key": "value"\n}' : 'key=value&key2=value2'}
          />
        </div>
      )}

      {/* Pattern Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          API Service Pattern
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { value: 'singleton', label: 'Singleton' },
            { value: 'generic', label: 'Generic Base Class' },
            { value: 'repository', label: 'Repository Pattern' },
            { value: 'functional', label: 'Functional Helpers' },
            { value: 'interceptor', label: 'Interceptor-Based' },
          ].map((pattern) => (
            <button
              key={pattern.value}
              onClick={() => handleChange('pattern', pattern.value)}
              className={`
                px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                ${
                  (config.pattern || 'singleton') === pattern.value
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {pattern.label}
            </button>
          ))}
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3">
        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
          Generated Swift Networking API Options
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.useAsyncAwait}
            onChange={(e) => handleChange('useAsyncAwait', e.target.checked)}
            className="rounded"
          />
          Include async/await API
        </label>

        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.includeCombineWrapper ?? true}
            onChange={(e) => handleChange('includeCombineWrapper', e.target.checked)}
            className="rounded"
          />
          Include Combine publisher wrapper
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Request Timeout (seconds)
          </label>
          <input
            type="number"
            min="1"
            max="300"
            value={config.timeout || 30}
            onChange={(e) => handleChange('timeout', parseInt(e.target.value) || 30)}
            className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.enableRetry || false}
            onChange={(e) => handleChange('enableRetry', e.target.checked)}
            className="rounded"
          />
          Enable Retry with Exponential Backoff
        </label>

        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.enableLogging || false}
            onChange={(e) => handleChange('enableLogging', e.target.checked)}
            className="rounded"
          />
          Enable Request/Response Logging
        </label>
      </div>
      
      {/* Test API Button */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={testAPI}
          disabled={isLoading || !config.baseURL}
          className={`
            w-full px-4 py-3 rounded-lg font-medium transition-all
            ${isLoading || !config.baseURL
              ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed'
              : 'bg-gradient-primary text-white hover:opacity-90'
            }
          `}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Testing API...
            </span>
          ) : (
            '🚀 Test API'
          )}
        </button>
      </div>
    </div>
  );
};

export default ApiGeneratorControls;

